import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";

type Language = "de" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  localizedPath: (path: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  de: {
    // Cookie Banner
    "cookie.text": "Diese Website nutzt Cookies, um dir eine bessere Nutzungserfahrung zu bieten, die Websitenutzung auszuwerten und unsere Marketingaktivit\u00E4ten zu optimieren. Mehr dazu in unserer",
    "cookie.link": "Datenschutzerklärung",
    "cookie.accept": "Akzeptieren",
    "cookie.reject": "Ablehnen",

    // Whitepaper Popup
    "popup.whitepaper.headline": "Micro Drama. Alles was Brands jetzt wissen müssen.",
    "popup.whitepaper.subtext": "Das Format wächst weltweit rasant — und die meisten Brands stehen noch am Rand. Dieser Guide zeigt, wie Micro Drama funktioniert, warum es wirkt, und wie ihr eure Brand mittendrin platziert.",
    "popup.whitepaper.cta": "Kostenlos lesen →",

    // Nav
    "nav.work": "Work",
    "nav.offer": "Offer",
    "nav.team": "Team",
    "nav.faq": "FAQ",
    "nav.blog": "Blog",
    "nav.presse": "Presse",
    "nav.contact": "Kontakt",
    "nav.cta": "Strategiegespräch buchen",
    "nav.whitepaper": "Whitepaper sichern",

    // Blog (kept)
    "blog.title": "Blog",
    "blog.subtitle": "Gedanken, Insights und Stories",
    "blog.backToOverview": "Zurück zur Übersicht",
    "blog.publishedOn": "Veröffentlicht am",
    "blog.by": "von",
    "blog.noPosts": "Noch keine Beiträge vorhanden.",
    "blog.errorLoading": "Fehler beim Laden der Beiträge.",
    "blog.postNotFound": "Beitrag nicht gefunden.",

    // Hero
    "hero.headline": "The Micro Drama Agency for Brands.",
    "hero.middle": "Audiences are done with ads. We find new ways to reach them.",
    "hero.subline": "We get your brand inside Micro Drama — Match. Create. Produce.",
    "hero.seo": "We are Germany's first agency specializing in Micro\u00A0Drama and branded serialized content for social media.",
    "hero.cta": "Strategiegespräch buchen",

    // Pain
    "pain.1": "Eure Zielgruppe skippt eure Ads nach 3 Sekunden.",
    "pain.2": "Euer Content bekommt Likes, aber keine Loyalität.",
    "pain.3": "Ihr produziert mehr als je zuvor.",
    "pain.4": "Und bleibt weniger im Gedächtnis als je zuvor.",
    "pain.bridge": "Aufmerksamkeit hat sich verlagert. Sie lebt heute in Geschichten — seriell, charaktergetrieben, mobile-first. Marken, die das verstehen, unterbrechen den Content nicht. Sie werden zum Content.",

    // Solution
    "solution.headline": "Wir machen keine Werbung oder Branded Content. Wir schaffen Einstiegspunkte.",
    "solution.intro": "Genau da, wo eure Zielgruppe gerade hängt: Micro Drama. Wir lassen Brands Teil davon werden — auf zwei Wegen.",
    "solution.match.title": "Match",
    "solution.match.description": "Wir verbinden eure Brand mit Micro Drama Serien in früher Entwicklungsphase — so wird sie organisch Teil der Story, nicht nachträglich integriert.",
    "solution.match.screen": "Wir verbinden eure Brand mit Micro Drama Serien in früher Entwicklungsphase — so wird sie organisch Teil der Story, nicht nachträglich integriert.",
    "solution.create.title": "Create",
    "solution.create.description": "Wir entwickeln eine originale Micro Drama Serie rund um eure Brand. Konzept, Skript, Produktion — eine Serie von Grund auf, die Menschen wirklich sehen wollen.",
    "solution.create.screen": "Wir entwickeln eine originale Micro Drama Serie rund um eure Brand. Konzept, Skript, Produktion — eine Serie von Grund auf, die Menschen wirklich sehen wollen.",
    "solution.bottom": "reelistiq × Eure Brand",

    // Offer
    "offer.headline": "Micro Drama für Brands.",
    "offer.subline": "Von der ersten Zusammenarbeit bis zur eigenen IP — jede Stufe bedeutet mehr Ownership, mehr Story, mehr Brand.",
    "offer.match.intro": "Wir verbinden eure Brand mit Micro Drama Serien in früher Entwicklungsphase — so wird sie organisch Teil der Story, nicht nachträglich integriert.",
    "offer.create.intro": "Wir entwickeln eine originale Micro Drama Serie speziell für eure Brand — Konzept, Skript, Produktion. Eure IP. Eure Story-Welt.",

    "offer.episodeSponsor.title": "Sponsorship — Episodes",
    "offer.episodeSponsor.subtitle": "Für Brands, die Micro Drama erstmal testen wollen.",
    "offer.episodeSponsor.bullet1": "8 Episoden, geschrieben in die Handlung",
    "offer.episodeSponsor.bullet2": "Brand Integration in ausgewählten Episoden",
    "offer.episodeSponsor.bullet3": "Story- und Zielgruppen-Matching durch uns",
    "offer.episodeSponsor.bullet4": "Fertige Assets für eure eigenen Kanäle",
    "offer.episodeSponsor.cta": "Gespräch starten",

    "offer.fullSponsor.title": "Full Sponsorship",
    "offer.fullSponsor.subtitle": "Für Brands, die eine komplette Serie tragen wollen.",
    "offer.fullSponsor.bullet1": "Gesamte Serie, von Episode 1 bis zum Finale",
    "offer.fullSponsor.bullet2": "Brand Integration über die gesamte Serie",
    "offer.fullSponsor.bullet3": "Exklusives Hauptsponsoring",
    "offer.fullSponsor.bullet4": "Fertige Assets für Social, Paid und PR",
    "offer.fullSponsor.cta": "Gespräch starten",

    "offer.pilot.title": "Pilot Season",
    "offer.pilot.subtitle": "Für Brands, die mit einer eigenen Serie starten wollen.",
    "offer.pilot.bullet1": "Von Strategie bis Schnitt: alles aus einer Hand",
    "offer.pilot.bullet2": "Drehbücher für 8 Episoden",
    "offer.pilot.bullet3": "Pre-Pro, Casting, Dreh & Post",
    "offer.pilot.bullet4": "Fertige Assets für Social & Paid",
    "offer.pilot.cta": "Gespräch starten",

    "offer.fullSeason.title": "Full Season",
    "offer.fullSeason.subtitle": "Für Brands, die eine vollständige Staffel produzieren wollen.",
    "offer.fullSeason.bullet1": "Von Strategie bis Schnitt: alles aus einer Hand",
    "offer.fullSeason.bullet2": "Drehbücher für 20 Episoden",
    "offer.fullSeason.bullet3": "Pre-Pro, Casting, Dreh & Post",
    "offer.fullSeason.bullet4": "Distribution Assets + Redaktionsplan",
    "offer.fullSeason.cta": "Gespräch starten",

    "offer.flagship.title": "Flagship",
    "offer.flagship.subtitle": "Für Brands, die das komplette Micro Drama Game spielen wollen.",
    "offer.flagship.bullet1": "100+ Episoden, mit Streaming-Potenzial",
    "offer.flagship.bullet2": "Umfang, Timeline und Struktur individuell",
    "offer.flagship.bullet3": "Begleitung von Konzept bis Plattform-Deal",
    "offer.flagship.cta": "Jetzt anfragen",

    "offer.bottomCta": "Nicht sicher, welches Paket passt? Lasst es uns in einem unverbindlichen Gespräch rausfinden.",
    "offer.bottomCtaButton": "Let's talk",
    "offer.ecosystem.headline": "Ecosystem",
    "offer.ecosystem.subline": "Wir denken Micro Drama nicht als einmaligen Content-Play, sondern als Ökosystem. Jede Maßnahme drumherum verlängert die Welt der Serie — und macht eure Brand zu einem festen Teil davon.",
    "offer.getStarted.headline": "Get Started",
    "offer.getStarted.subline": "Bereit, eure Marke in eine Serie zu bringen? Lasst uns sprechen.",
    "offer.getStarted.button": "Kontakt aufnehmen",

    // Work
    "work.headline": "Was wir bereits gemacht haben.",
    "work.case.title": "Aminho — The Challenge",
    "work.case.collab": "Constantin Entertainment × Skechers",
    "work.case.description": "Deutschlands erstes Micro-Drama. Konzipiert für TikTok, nicht für Streaming. Skechers wurde organisch Teil der Story — ohne Werbelogik, ohne Unterbrechung.",
    "work.case.description2": "Das erste Branded Micro Drama in Deutschland — entwickelt von reelistiq.",
    "work.case.deliveredLabel": "Was wir geliefert haben:",
    "work.case.delivered1": "Creative Concept & vollständiges Drehbuch",
    "work.case.delivered2": "Strategisches Creative Consulting von Konzept bis Produktion",
    "work.case.delivered3": "Brand als organischer Teil der Story",
    "work.case.resultsLabel": "Ergebnisse:",
    "work.case.metric1": "1,1 Mio.",
    "work.case.metric1Label": "Views",
    "work.case.metric2": "Ø 55.000",
    "work.case.metric2Label": "Views pro Episode",
    "work.case.metric3": "+218%",
    "work.case.metric3Label": "Views Ep.1 → Finale",
    "work.case.press": "Featured in Campaign Germany & Business Punk",
    "work.case.cta": "Case Study ansehen",
    "work.clients.organic": "Organisches Video-Marketing für:",
    "work.clients.organicList": "KIT · Bundesministerium für Bildung und Forschung · NoPlanetB · everdrop · optiplan · Brick + Bone · Nalewo · Counting the Apples · everydays · SportPlus",
    "work.clients.drama": "Micro Drama:",
    "work.clients.dramaList": "Skechers · Constantin Entertainment",
    "work.clients.film": "Unsere Film-Arbeit läuft auf ARD, ZDF und KiKA.",
    "work.indev.headline": "In Entwicklung.",
    "work.indev.cta": "Ihr wollt eure Brand in eine dieser Welten bringen?",
    "work.indev.ctaButton": "Jetzt anfragen",

    // Team
    "team.headline": "Team",
    "team.p1": "reelistiq ist Deutschlands erste Micro Drama Agentur — gegründet von einer Regisseurin und gewachsen zu einem Team aus Filmschaffenden, Autor:innen und Kreativen, die Micro Drama von innen kennen.",
    "team.p2": "Für die Umsetzung bringen wir strategische Partner mit: Glutamat für Creative & Produktion, Friday Film für Produktion, Dock11 für Virtual Production — sowie einen großen Pool an freien Talents, die in diesem Format zuhause sind.",

    // FAQ
    "faq.headline": "Fragen, die wir schon kennen.",
    "faq.q1": "Was ist Micro Drama?",
    "faq.a1": "Micro Drama ist serieller, fiktionaler Content — speziell für mobile, vertikale Screens entwickelt. Kurze Episoden, echte Charaktere, echte Dramaturgie. Denkt an eine Netflix-Serie, die direkt im Feed lebt. Das Format wächst weltweit rasant — Deutschland steht noch am Anfang.",
    "faq.q2": "Was ist der Unterschied zwischen Vertical Drama und Micro Drama?",
    "faq.a2": "Micro Drama beschreibt den Inhalt — extrem kurze Episoden (60–90 Sekunden), hochverdichtete Dramaturgie, serialisiert über viele Folgen. Das Format kann technisch auch in 16:9 produziert werden. Vertical Drama beschreibt die Form — für das 9:16-Hochformat produziert, native für den Smartphone-Bildschirm. In der Praxis meinen beide Begriffe heute meist dasselbe: das \"Vertical\" ist das visuelle Gefäß, das \"Micro\" der Inhalt darin.",
    "faq.q3": "Wir machen doch schon guten Videocontent — was ist der Unterschied?",
    "faq.a3": "Einzelne Videos, auch starke, werden konsumiert und vergessen. Micro Drama baut etwas auf: Charaktere, denen man folgt. Eine Welt, in die man zurückkommt. Die Brand ist nicht Absender eines Posts, sondern Teil einer Geschichte. Das schafft nachhaltigere Markenbindung und deutlich höhere Verweildauer.",
    "faq.q4": "Was liefert ihr konkret?",
    "faq.a4": "Das hängt vom Paket ab — aber grundsätzlich begleiten wir das gesamte Projekt: von der ersten Idee über Creative Concept und Skript bis zur fertigen Serie, inklusive Assets für Social, Paid und PR. Beim Match-Modell übernehmen wir zusätzlich das Matching mit passenden Serien und begleiten die Integration.",
    "faq.q5": "Wie läuft eine Zusammenarbeit ab?",
    "faq.a5": "Alles beginnt mit einem Strategiegespräch. Wir verstehen eure Brand, eure Ziele und eure Zielgruppe — und empfehlen den richtigen Weg. Danach entwickeln wir gemeinsam Konzept und Skript, koordinieren die Produktion und begleiten euch bis zur fertigen Episode.",
    "faq.q6": "Für welche Branchen ist das geeignet?",
    "faq.a6": "Für Brands, die eine Geschichte haben — und eine Zielgruppe, die auf Social Media zuhause ist. Wir haben mit Consumer Brands, Sportmarken und Unterhaltungsunternehmen gearbeitet. Entscheidend ist nicht die Branche, sondern die Neugier, Content neu zu denken.",
    "faq.q7": "Wie schnell kann es losgehen?",
    "faq.a7": "Schnell. Nach dem Kick-off könnt ihr innerhalb weniger Tage ein konkretes Konzept in den Händen halten. Die Produktionszeit hängt vom Umfang und Format ab — wir besprechen das gemeinsam im ersten Call.",
    "faq.q8": "Ist Micro Drama nur für große Brands mit großem Budget?",
    "faq.a8": "Nein. Micro Drama ist skalierbar — vom ersten Episoden-Sponsoring bis zur eigenen 100-Episoden-Serie. Der richtige Einstiegspunkt hängt von euren Zielen ab, nicht vom Budget. Unser erstes Gespräch ist kostenlos — und danach wisst ihr genau, was für euch Sinn macht.",

    // Contact Form
    "contact.form.headline": "Kontakt",
    "contact.form.subline": "Erzählt uns von eurem Projekt — wir melden uns innerhalb von 48 Stunden.",
    "contact.form.company": "Unternehmen",
    "contact.form.firstName": "Vorname",
    "contact.form.lastName": "Nachname",
    "contact.form.email": "E-Mail-Adresse",
    "contact.form.message": "Nachricht",
    "contact.form.referralLabel": "Ihr kennt uns durch… (optional)",
    "contact.form.referralPlaceholder": "— Bitte wählen —",
    "contact.form.referral.search": "Google / Suchmaschine",
    "contact.form.referral.social": "Social Media",
    "contact.form.referral.recommendation": "Empfehlung",
    "contact.form.referral.event": "Event / Konferenz",
    "contact.form.referral.press": "Presse / Artikel",
    "contact.form.referral.other": "Sonstiges",
    "contact.form.submit": "Nachricht senden",
    "contact.form.sending": "Wird gesendet…",
    "contact.form.success": "Vielen Dank! Wir melden uns bei euch.",
    "contact.form.error": "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
    "contact.form.recaptchaNotice": "Diese Seite ist durch reCAPTCHA geschützt. Es gelten die Datenschutzbestimmungen und Nutzungsbedingungen von Google.",

    // CTA
    "cta.headline": "Bereit für eure erste Episode?",
    "cta.subline": "Jede große Serie beginnt mit einem Gespräch.",
    "cta.button": "Strategiegespräch buchen",
    "cta.email": "Oder schreibt uns direkt:",

    // Teaser
    "teaser.headline": "Entdecken",
    "teaser.cta": "Mehr erfahren",
    "teaser.offer.title": "Offer",
    "teaser.offer.desc": "Von der ersten Brand Integration bis zur eigenen IP — findet den richtigen Einstiegspunkt.",
    "teaser.work.title": "Work",
    "teaser.work.desc": "Wie wir Brands in Micro Drama Serien gebracht haben — Ergebnisse, Metriken und Behind-the-Scenes.",
    "teaser.team.title": "Team",
    "teaser.team.desc": "Filmschaffende, Autor:innen und Kreative — das Team hinter reelistiq.",
    "teaser.faq.title": "FAQ",
    "teaser.faq.desc": "Alles was ihr über Micro Drama, unsere Pakete und die Zusammenarbeit wissen müsst.",

    "footer.tagline": "reelistiq — Deutschlands erste Micro Drama Agency.",
    "footer.legal": "Impressum",
    "footer.privacy": "Datenschutz",
  },
  en: {
    // Cookie Banner
    "cookie.text": "This website uses cookies to enhance your browsing experience, evaluate site usage, and optimize our marketing activities. Learn more in our",
    "cookie.link": "Privacy Policy",
    "cookie.accept": "Accept",
    "cookie.reject": "Decline",

    // Whitepaper Popup
    "popup.whitepaper.headline": "Micro Drama. What Every Brand Needs to Know.",
    "popup.whitepaper.subtext": "The format is exploding globally — and most brands are still on the sidelines. This guide breaks down how Micro Drama works, why it performs, and how to get your brand inside it.",
    "popup.whitepaper.cta": "Read for free →",

    // Nav
    "nav.work": "Work",
    "nav.offer": "Offer",
    "nav.team": "Team",
    "nav.faq": "FAQ",
    "nav.blog": "Blog",
    "nav.presse": "Press",
    "nav.contact": "Contact",
    "nav.cta": "Book a strategy call",
    "nav.whitepaper": "Get Whitepaper",

    // Blog (kept)
    "blog.title": "Blog",
    "blog.subtitle": "Thoughts, insights and stories",
    "blog.backToOverview": "Back to overview",
    "blog.publishedOn": "Published on",
    "blog.by": "by",
    "blog.noPosts": "No posts yet.",
    "blog.errorLoading": "Error loading posts.",
    "blog.postNotFound": "Post not found.",

    // Hero
    "hero.headline": "The Micro Drama Agency for Brands.",
    "hero.middle": "Audiences are done with ads. We find new ways to reach them.",
    "hero.subline": "We get your brand inside Micro Drama — Match. Create. Produce.",
    "hero.seo": "We are Germany's first agency specializing in Micro\u00A0Drama and branded serialized content for social media.",
    "hero.cta": "Book a strategy call",

    // Pain
    "pain.1": "Your audience skips your ads in 3 seconds.",
    "pain.2": "Your content gets likes. Not loyalty.",
    "pain.3": "You're producing more than ever.",
    "pain.4": "And remembered less than ever.",
    "pain.bridge": "Attention has moved. It lives inside stories now — serialized, character-driven, mobile-first. Brands that understand this don't interrupt the content. They become it.",

    // Solution
    "solution.headline": "We don't make ads or branded content. We make entry points.",
    "solution.intro": "Right where your audience actually is: Micro Drama. Two ways in.",
    "solution.match.title": "Match",
    "solution.match.description": "We connect your brand with micro drama series in early development — so it becomes an organic part of the story, not an afterthought.",
    "solution.match.screen": "We connect your brand with micro drama series in early development — so it becomes an organic part of the story, not an afterthought.",
    "solution.create.title": "Create",
    "solution.create.description": "We develop an original micro drama series built around your brand. Concept, script, production — a series from scratch that audiences actually want to watch.",
    "solution.create.screen": "We develop an original micro drama series built around your brand. Concept, script, production — a series from scratch that audiences actually want to watch.",
    "solution.bottom": "reelistiq × Your Brand",

    // Offer
    "offer.headline": "Micro Drama for Brands.",
    "offer.subline": "From your first collaboration to owning your own IP — each step means more story, more brand, more yours.",
    "offer.match.intro": "We connect your brand with micro drama series in early development — so it becomes an organic part of the story, not an afterthought.",
    "offer.create.intro": "We develop an original micro drama series built specifically around your brand — concept, script, production. Your IP. Your story world.",

    "offer.episodeSponsor.title": "Sponsorship — Episodes",
    "offer.episodeSponsor.subtitle": "For brands that want to test Micro Drama first.",
    "offer.episodeSponsor.bullet1": "8 episodes, written into the storyline",
    "offer.episodeSponsor.bullet2": "Brand integration across selected episodes",
    "offer.episodeSponsor.bullet3": "Story and audience matching by us",
    "offer.episodeSponsor.bullet4": "Finished assets for your own channels",
    "offer.episodeSponsor.cta": "Get started",

    "offer.fullSponsor.title": "Full Sponsorship",
    "offer.fullSponsor.subtitle": "For brands ready to back an entire series.",
    "offer.fullSponsor.bullet1": "Entire series, from episode 1 to the finale",
    "offer.fullSponsor.bullet2": "Brand integration across the entire series",
    "offer.fullSponsor.bullet3": "Exclusive lead sponsorship",
    "offer.fullSponsor.bullet4": "Finished assets for social, paid and PR",
    "offer.fullSponsor.cta": "Get started",

    "offer.pilot.title": "Pilot Season",
    "offer.pilot.subtitle": "For brands ready to launch their own series.",
    "offer.pilot.bullet1": "From strategy to final cut: all from one source",
    "offer.pilot.bullet2": "Scripts for 8 episodes",
    "offer.pilot.bullet3": "Pre-pro, casting, shoot & post",
    "offer.pilot.bullet4": "Finished assets for social & paid",
    "offer.pilot.cta": "Get started",

    "offer.fullSeason.title": "Full Season",
    "offer.fullSeason.subtitle": "For brands ready to produce a full season.",
    "offer.fullSeason.bullet1": "From strategy to final cut: all from one source",
    "offer.fullSeason.bullet2": "Scripts for 20 episodes",
    "offer.fullSeason.bullet3": "Pre-pro, casting, shoot & post",
    "offer.fullSeason.bullet4": "Distribution assets + editorial plan",
    "offer.fullSeason.cta": "Get started",

    "offer.flagship.title": "Flagship",
    "offer.flagship.subtitle": "For brands ready to play the full micro drama game.",
    "offer.flagship.bullet1": "100+ episodes, with streaming potential",
    "offer.flagship.bullet2": "Scope, timeline and structure — fully custom",
    "offer.flagship.bullet3": "Support from concept to platform deal",
    "offer.flagship.cta": "Get started",

    "offer.bottomCta": "Not sure which package fits? Let's figure it out in a no-obligation conversation.",
    "offer.bottomCtaButton": "Let's talk",
    "offer.ecosystem.headline": "Ecosystem",
    "offer.ecosystem.subline": "We don't think of Micro Drama as a one-time content play, but as an ecosystem. Every element around it extends the world of the series — and makes your brand a permanent part of it.",
    "offer.getStarted.headline": "Get Started",
    "offer.getStarted.subline": "Ready to put your brand inside a series? Let's talk.",
    "offer.getStarted.button": "Get in touch",

    // Work
    "work.headline": "What we've already done.",
    "work.case.title": "Aminho — The Challenge",
    "work.case.collab": "Constantin Entertainment × Skechers",
    "work.case.description": "Germany's first micro-drama. Made for TikTok, not a streaming platform. Skechers didn't sponsor the story — it became part of it.",
    "work.case.description2": "The first branded micro drama in Germany — developed by reelistiq.",
    "work.case.deliveredLabel": "What we brought to the table:",
    "work.case.delivered1": "Creative concept & full script",
    "work.case.delivered2": "Strategic creative consulting from concept to production",
    "work.case.delivered3": "Brand as an organic part of the story",
    "work.case.resultsLabel": "Results:",
    "work.case.metric1": "1.1 million",
    "work.case.metric1Label": "Views",
    "work.case.metric2": "Avg. 55,000",
    "work.case.metric2Label": "Views per episode",
    "work.case.metric3": "+218%",
    "work.case.metric3Label": "Views Ep.1 → Finale",
    "work.case.press": "Featured in Campaign Germany & Business Punk",
    "work.case.cta": "View case study",
    "work.clients.organic": "Organic video marketing for:",
    "work.clients.organicList": "KIT · Federal Ministry of Education and Research · NoPlanetB · everdrop · optiplan · Brick + Bone · Nalewo · Counting the Apples · everydays · SportPlus",
    "work.clients.drama": "Micro Drama:",
    "work.clients.dramaList": "Skechers · Constantin Entertainment",
    "work.clients.film": "Our film work airs on ARD, ZDF and KiKA.",
    "work.indev.headline": "In Development.",
    "work.indev.cta": "Want to get your brand inside one of these worlds?",
    "work.indev.ctaButton": "Get in touch",

    // Team
    "team.headline": "Team",
    "team.p1": "reelistiq is Germany's first Micro Drama agency — founded by a director and built into a team of filmmakers, writers and creatives who know micro drama from the inside out.",
    "team.p2": "For production, we work with a tight circle of strategic partners — Glutamat for creative & production, Friday Film for production, Dock11 for virtual production — plus a wide pool of freelance talent who live and breathe this format.",

    // FAQ
    "faq.headline": "Questions we already know.",
    "faq.q1": "What is Micro Drama?",
    "faq.a1": "Micro drama is serialized fictional content — developed specifically for mobile, vertical screens. Short episodes, real characters, real dramaturgy. Think Netflix — but native to your feed. The format is exploding globally. Germany is just getting started.",
    "faq.q2": "What's the difference between Vertical Drama and Micro Drama?",
    "faq.a2": "Micro Drama describes the content — extremely short episodes (60–90 seconds), high-density dramaturgy, serialized across many installments. Technically it can also be produced in 16:9. Vertical Drama describes the form — produced in 9:16 portrait format, native to the smartphone screen. In practice, the two terms are used interchangeably — \"Vertical\" is the container, \"Micro\" is what's inside.",
    "faq.q3": "We already make great video content — what's the difference?",
    "faq.a3": "Even great videos get watched and forgotten. Micro drama builds something that sticks: characters people follow, a world they come back to. Your brand isn't interrupting the feed — it's woven into the story. That's what drives real affinity and time spent.",
    "faq.q4": "What do we actually deliver?",
    "faq.a4": "It depends on the package — but as a rule, we run the whole thing: from initial idea through concept and script to a finished series, including assets for social, paid and PR. On the Match side, we also handle finding the right series and managing the integration.",
    "faq.q5": "How does the process work?",
    "faq.a5": "It starts with a strategy call. We get under the skin of your brand, your goals, your audience — and tell you honestly which path makes sense. From there, we develop concept and script, coordinate production, and see it through to the finished episode.",
    "faq.q6": "What industries do you work with?",
    "faq.a6": "Any brand with a story to tell — and an audience that lives on social. We've worked with consumer brands, sports brands and entertainment companies. Industry doesn't matter. Curiosity does.",
    "faq.q7": "How fast can you move?",
    "faq.a7": "Fast. Once we kick off, you'll have a concrete concept within days. How long production takes depends on scope and format — we figure that out together on the first call.",
    "faq.q8": "Is Micro Drama only for big brands with big budgets?",
    "faq.a8": "No. Micro Drama scales — from a first episode sponsorship to your own 100-episode series. The right entry point depends on your goals, not your budget. Our first call is free — and afterwards you'll know exactly what makes sense for you.",

    // Contact Form
    "contact.form.headline": "Contact",
    "contact.form.subline": "Tell us about your project — we'll get back to you within 48 hours.",
    "contact.form.company": "Company",
    "contact.form.firstName": "First name",
    "contact.form.lastName": "Last name",
    "contact.form.email": "Email address",
    "contact.form.message": "Message",
    "contact.form.referralLabel": "How did you hear about us? (optional)",
    "contact.form.referralPlaceholder": "— Please select —",
    "contact.form.referral.search": "Google / Search engine",
    "contact.form.referral.social": "Social Media",
    "contact.form.referral.recommendation": "Recommendation",
    "contact.form.referral.event": "Event / Conference",
    "contact.form.referral.press": "Press / Article",
    "contact.form.referral.other": "Other",
    "contact.form.submit": "Send message",
    "contact.form.sending": "Sending…",
    "contact.form.success": "Thank you! We'll be in touch soon.",
    "contact.form.error": "Something went wrong. Please try again.",
    "contact.form.recaptchaNotice": "This site is protected by reCAPTCHA. Google's Privacy Policy and Terms of Service apply.",

    // CTA
    "cta.headline": "Ready for your first episode?",
    "cta.subline": "Every great series starts with a conversation.",
    "cta.button": "Book a strategy call",
    "cta.email": "Or reach out directly:",

    // Teaser
    "teaser.headline": "Explore",
    "teaser.cta": "Learn more",
    "teaser.offer.title": "Offer",
    "teaser.offer.desc": "From your first brand integration to your own IP — find the right entry point.",
    "teaser.work.title": "Work",
    "teaser.work.desc": "How we've placed brands inside Micro Drama series — results, metrics and behind-the-scenes.",
    "teaser.team.title": "Team",
    "teaser.team.desc": "Filmmakers, writers and creatives — the team behind reelistiq.",
    "teaser.faq.title": "FAQ",
    "teaser.faq.desc": "Everything you need to know about Micro Drama, our packages and working with us.",

    // Footer
    "footer.tagline": "reelistiq — Germany's first Micro Drama Agency.",
    "footer.legal": "Legal notice",
    "footer.privacy": "Privacy policy",
  },
};

function detectBrowserLanguage(): Language {
  const browserLang = navigator.language || (navigator as any).userLanguage || "de";
  return browserLang.startsWith("de") ? "de" : "en";
}

function getLanguageFromPath(pathname: string): Language | null {
  const match = pathname.match(/^\/(de|en)(\/|$)/);
  return match ? (match[1] as Language) : null;
}

function stripLanguagePrefix(pathname: string): string {
  const stripped = pathname.replace(/^\/(de|en)(\/|$)/, "/");
  return stripped === "" ? "/" : stripped;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  
  const langFromPath = getLanguageFromPath(location.pathname);
  const [language, setLanguageState] = useState<Language>(langFromPath || "de");

  // Sync language from URL on route changes
  useEffect(() => {
    const lang = getLanguageFromPath(location.pathname);
    if (lang && lang !== language) {
      setLanguageState(lang);
    }
  }, [location.pathname]);

  const setLanguage = useCallback((lang: Language) => {
    if (lang === language) return;
    const pathWithoutLang = stripLanguagePrefix(location.pathname);
    const newPath = `/${lang}${pathWithoutLang === "/" ? "" : pathWithoutLang}`;
    setLanguageState(lang);
    navigate(newPath + location.search + location.hash, { replace: true });
  }, [language, location, navigate]);

  const localizedPath = useCallback((path: string): string => {
    // For hash links, return as-is
    if (path.startsWith("#")) return path;
    // Ensure path starts with /
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `/${language}${cleanPath === "/" ? "" : cleanPath}`;
  }, [language]);

  const t = useCallback((key: string): string => {
    return translations[language][key] || key;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, localizedPath }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

export { detectBrowserLanguage, getLanguageFromPath };
